/**
 * OS-owner data-access helpers (PRD-004 Batch 2, owner surface).
 *
 * Thin supabase-js wrappers for the OS engagement owner's private surface.
 * Mirrors lib/portal.ts: no ORM (ARCH-001); RLS enforces ownership
 * (ARCH-006 / migration 0026 `os_engagements_owner_select`), so these helpers
 * never pass an owner filter on read. The owner reads ONLY; the OS lifecycle
 * is operator-driven (PRD-004 §4 PR-OS-01, scoping pass §8 TECH-OS-05: no owner
 * INSERT/UPDATE policy exists).
 *
 * Route A identity model (scoping pass §1): an OS owner IS a `subscribers` row
 * in `status='pending'` / `tier='unset'`, with an `os_engagements` row FK'd to
 * `subscribers.id`. The existing portal entry guard bounces `pending`, so the
 * OS owner reaches this data through the separate `/os` surface and its own
 * guard (lib/os-guard.ts).
 *
 * The shared @studio/api-schemas package does not yet carry OS types (Batch 1
 * shipped migrations only, no shared TS schema). The owner-facing read shapes
 * are declared here against the live `os_engagements` columns (migration 0026 /
 * scoping pass §3 DATA-OS-01). When a shared schema lands, these can move into
 * the package; for now they live with their only consumer (this surface).
 */

import {
  osHandoffContentSchema,
  type OsHandoffContent,
  type VideoProvider,
} from '@studio/api-schemas';
import { supabase } from './supabase.js';

/**
 * The `os_status` lifecycle values (scoping pass §3 / §4 PR-OS-01). Linear with
 * two operator-marked booking checkpoints; the owner never drives a transition.
 */
export type OsStatus =
  | 'intake'
  | 'acknowledged'
  | 'scoped'
  | 'session1_booked'
  | 'discovery_done'
  | 'building'
  | 'handoff_ready'
  | 'session2_booked'
  | 'installed'
  | 'open';

/**
 * The owner-readable projection of an `os_engagements` row. The columns the
 * `os_engagements_owner_select` policy (migration 0026) exposes to the owner.
 * Operator-scratch payment fields (`payment_state`, `price_*`) are not read by
 * the owner surface; the status line and the two-session spine are driven by
 * `status`, `scope`, `entry_workflow`, `ready_by`, and the booking timestamps.
 */
export interface OwnOsEngagement {
  id: string;
  status: OsStatus;
  scope: string | null;
  entry_workflow: string | null;
  ready_by: string | null;
  handoff_deliverable_id: string | null;
  session1_booking_url: string | null;
  session2_booking_url: string | null;
  acknowledged_at: string | null;
  scoped_at: string | null;
  session1_booked_at: string | null;
  discovery_done_at: string | null;
  handoff_ready_at: string | null;
  session2_booked_at: string | null;
  installed_at: string | null;
  opened_at: string | null;
  created_at: string;
  updated_at: string;
}

const OS_ENGAGEMENT_COLUMNS =
  'id, status, scope, entry_workflow, ready_by, handoff_deliverable_id, session1_booking_url, session2_booking_url, acknowledged_at, scoped_at, session1_booked_at, discovery_done_at, handoff_ready_at, session2_booked_at, installed_at, opened_at, created_at, updated_at';

/**
 * The caller's most recent non-deleted `os_engagements` row, or null.
 *
 * RLS `os_engagements_owner_select` (migration 0026) scopes rows to
 * `is_owner(owner_subscriber_id) and deleted_at is null`, so no owner filter is
 * passed here. An owner can in principle hold more than one engagement over
 * time (a second OS run; `owner_subscriber_id` is intentionally non-unique per
 * scoping pass §3), so we order by recency and take the most recent. Returns
 * null when the caller has no engagement (the guard treats that as not-an-owner).
 */
export async function fetchOwnOsEngagement(): Promise<OwnOsEngagement | null> {
  const { data, error } = await supabase
    .from('os_engagements')
    .select(OS_ENGAGEMENT_COLUMNS)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as OwnOsEngagement | null) ?? null;
}

// ---- Handoff view (Batch 3, owner surface) ------------------------------
//
// The handoff is a real deliverables row with task_id IS NULL (migration 0031),
// reached by the owner through the deliverables_os_owner_select policy gated on
// os_status >= 'handoff_ready'. Its downloadable artifacts live in
// os_handoff_files (read via os_handoff_files_owner_select, migration 0027) and
// the private 'os-handoffs' storage bucket (storage RLS, migration 0030). RLS
// does all the scoping, so these helpers pass no owner filter; an engagement
// not yet at handoff_ready simply returns no rows.
//
// The owner module stays decoupled from the operator surface: it computes the
// "at or past handoff_ready" gate from its own ordered list (below) rather than
// importing the operator OS_STATUS_ORDER.

/**
 * os_status in lifecycle order (migration 0026 enum order). Owner-local copy so
 * this surface never imports operator code. Used to compute the
 * "at or past handoff_ready" gate the handoff view keys on.
 */
export const OS_STATUS_LIFECYCLE: readonly OsStatus[] = [
  'intake',
  'acknowledged',
  'scoped',
  'session1_booked',
  'discovery_done',
  'building',
  'handoff_ready',
  'session2_booked',
  'installed',
  'open',
];

/**
 * True when `status` is at or past `handoff_ready` in the lifecycle order. This
 * is the same enum-ordinal comparison the RLS policies (0027/0030/0031) use to
 * gate owner read of the handoff deliverable and its files.
 */
export function isAtOrPastHandoffReady(status: OsStatus): boolean {
  const cur = OS_STATUS_LIFECYCLE.indexOf(status);
  const gate = OS_STATUS_LIFECYCLE.indexOf('handoff_ready');
  return cur >= 0 && cur >= gate;
}

/**
 * The owner-readable projection of the handoff deliverable row. Only the fields
 * the owner surface composes: the typed content body, and the walkthrough video
 * (rendered ALWAYS-ON for the OS owner per TECH-OS-02, independent of tier).
 */
export interface OwnHandoffDeliverable {
  id: string;
  content: OsHandoffContent;
  content_schema_version: number;
  video_url: string | null;
  video_provider: VideoProvider | null;
}

/**
 * The owner-readable handoff deliverable, via deliverables_os_owner_select
 * (migration 0031). Returns null when the engagement is not yet at
 * handoff_ready or no handoff deliverable exists. The content is parsed against
 * the shared osHandoffContentSchema so a malformed row degrades to null rather
 * than rendering garbage.
 */
export async function fetchOwnHandoffDeliverable(
  deliverableId: string,
): Promise<OwnHandoffDeliverable | null> {
  const { data, error } = await supabase
    .from('deliverables')
    .select('id, content, content_schema_version, video_url, video_provider')
    .eq('id', deliverableId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const row = data as {
    id: string;
    content: unknown;
    content_schema_version: number;
    video_url: string | null;
    video_provider: VideoProvider | null;
  };
  const parsed = osHandoffContentSchema.safeParse(row.content);
  if (!parsed.success) return null;
  return {
    id: row.id,
    content: parsed.data,
    content_schema_version: row.content_schema_version,
    video_url: row.video_url,
    video_provider: row.video_provider,
  };
}

/**
 * A downloadable handoff artifact (os_handoff_files row). Ordered by sort_order.
 */
export interface OwnHandoffFile {
  id: string;
  filename: string;
  storage_path: string;
  mime_type: string;
  size_bytes: number | null;
  label: string | null;
  sort_order: number;
}

const OS_HANDOFF_FILE_COLUMNS =
  'id, filename, storage_path, mime_type, size_bytes, label, sort_order';

/**
 * The handoff files for a deliverable, ordered by sort_order, via
 * os_handoff_files_owner_select (migration 0027). RLS scopes the rows to the
 * owner of an engagement at or past handoff_ready, so no owner filter is passed.
 */
export async function fetchOwnHandoffFiles(
  deliverableId: string,
): Promise<OwnHandoffFile[]> {
  const { data, error } = await supabase
    .from('os_handoff_files')
    .select(OS_HANDOFF_FILE_COLUMNS)
    .eq('deliverable_id', deliverableId)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data ?? []) as OwnHandoffFile[];
}

/**
 * The signed-URL TTL for a handoff download, in seconds (one hour). Long enough
 * for the owner to click through every file in one sitting; short enough that a
 * leaked URL expires quickly. ARCH-002: the owner's own authenticated client
 * mints this; storage.objects RLS (migration 0030) is the actual access gate.
 */
const HANDOFF_SIGNED_URL_TTL_SECONDS = 3600;

/**
 * Mint a signed URL for one handoff file. The storage_path is the bucket key
 * (= os_handoff_files.storage_path = storage.objects.name). Storage RLS enforces
 * access; a caller without the owner grant gets an error, surfaced honestly.
 */
export async function createHandoffFileSignedUrl(
  storagePath: string,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from('os-handoffs')
    .createSignedUrl(storagePath, HANDOFF_SIGNED_URL_TTL_SECONDS);
  if (error) throw error;
  if (!data?.signedUrl) {
    throw new Error('Could not mint a download link for this file.');
  }
  return data.signedUrl;
}
